import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, type Period } from '../api'
import { useAuth } from '../auth'
import { Card, ErrorNote, Loading, Note, Pill } from '../components/ui'

interface FiscalYear {
  fy_code: string
  start_date: string
  end_date: string
  status: string
  period_count: number
}

/** "2026-27" for a year beginning 1 April 2026. Two digits, so 2099 gives 00. */
function fyCodeFor(startYear: number): string {
  return `${startYear}-${String((startYear + 1) % 100).padStart(2, '0')}`
}

/**
 * The financial year containing today, and the two either side of it.
 *
 * An Indian financial year begins in April, so January to March belongs to the
 * year named for the *previous* calendar year. Offering the wrong default here
 * is how somebody opens 2027-28 in February and finds their January entries
 * have nowhere to go.
 */
function suggestedYears(today = new Date()): string[] {
  const startYear = today.getMonth() >= 3 ? today.getFullYear() : today.getFullYear() - 1
  return [startYear - 1, startYear, startYear + 1].map(fyCodeFor)
}

export default function Periods() {
  const { can } = useAuth()
  const queryClient = useQueryClient()
  const [year, setYear] = useState(() => suggestedYears()[1])

  const { data, isLoading, error } = useQuery({
    queryKey: ['periods'],
    queryFn: () => api.get<Period[]>('/periods'),
  })

  const years = useQuery({
    queryKey: ['fiscal-years'],
    queryFn: () => api.get<FiscalYear[]>('/periods/years'),
  })

  const openYear = useMutation({
    mutationFn: (fy_code: string) => api.post<FiscalYear>('/periods/years', { fy_code }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['periods'] })
      void queryClient.invalidateQueries({ queryKey: ['fiscal-years'] })
    },
  })

  if (isLoading) return <Loading what="Loading periods" />
  const rows = data ?? []
  const openedYears = years.data ?? []
  const canOpenYear = can('org.fiscal_year.configure')

  return (
    <>
      <ErrorNote error={error} />
      {rows.length === 0 && (
        <Note kind="warn">
          No periods exist, so nothing can be posted yet. All twelve months are created
          when a financial year is opened — they are never conjured on demand, so a stray
          date cannot invent one.
          {!canOpenYear && ' Opening a year is an administrator’s job.'}
        </Note>
      )}

      {canOpenYear && (
        <Card
          title="Financial years"
          hint="April to March; twelve months, opened together"
        >
          <ErrorNote error={openYear.error} />
          {openedYears.length === 0 ? (
            <p className="muted">
              None yet. This is the first thing to do on a new set of books.
            </p>
          ) : (
            <p className="muted">
              Open:{' '}
              {openedYears.map((y) => (
                <span key={y.fy_code} className="mono">
                  {y.fy_code}{' '}
                </span>
              ))}
            </p>
          )}
          <div className="row">
            <label className="shrink">
              <span className="caption">Year to open</span>
              <select value={year} onChange={(e) => setYear(e.target.value)}>
                {suggestedYears().map((code) => (
                  <option key={code} value={code}>
                    {code}
                  </option>
                ))}
              </select>
            </label>
            <div className="shrink">
              <button
                className="primary"
                disabled={
                  openYear.isPending || openedYears.some((y) => y.fy_code === year)
                }
                onClick={() => openYear.mutate(year)}
              >
                {openedYears.some((y) => y.fy_code === year)
                  ? `${year} is already open`
                  : `Open ${year}`}
              </button>
            </div>
          </div>
        </Card>
      )}

      <Card title="Accounting periods" hint={`${rows.length} months`} tight>
        <table>
          <thead>
            <tr>
              <th>Month</th>
              <th>From</th>
              <th>To</th>
              <th>Status</th>
              <th className="num">Reopened</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {rows.map((p) => (
              <tr key={p.period_label}>
                <td className="mono">{p.period_label}</td>
                <td className="muted">{p.period_start}</td>
                <td className="muted">{p.period_end}</td>
                <td>
                  <Pill value={p.status} />
                </td>
                <td className="num">
                  {p.reopened_count > 0 ? (
                    p.reopened_count
                  ) : (
                    <span className="faint">—</span>
                  )}
                </td>
                <td className="num">
                  <Link to={`/books/periods/${p.period_label}`}>Open</Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      <Card title="What the four states mean">
        <table>
          <tbody>
            <tr>
              <td style={{ width: 130 }}>
                <Pill value="open" />
              </td>
              <td className="muted">Anyone with the permission can post.</td>
            </tr>
            <tr>
              <td>
                <Pill value="under_review" />
              </td>
              <td className="muted">
                The trial balance has been drawn and is being reconciled. Everyone else stops
                posting so the figures hold still; whoever is closing can still post the
                adjustments they find.
              </td>
            </tr>
            <tr>
              <td>
                <Pill value="closed" />
              </td>
              <td className="muted">
                A discipline — somebody decided the month was done. Can be reopened, with a
                written reason, and the count is kept.
              </td>
            </tr>
            <tr>
              <td>
                <Pill value="locked" />
              </td>
              <td className="muted">
                A fact — returns were filed on these numbers. There is no route back. A wrong
                locked month is corrected by an entry in an open one; that is what a revised
                return is.
              </td>
            </tr>
          </tbody>
        </table>
      </Card>
    </>
  )
}
